import { useAgentContext } from '@src/contexts/agent-context';
import { type ProjectListItem } from '@sdk';
import { lazyAssets, LazyAsset } from '@sdk/lazy';
import { useLazyAsset } from '@sdk/react/hooks/useLazyAsset';
import { useEffect } from 'react';
const NO_PROJECTS: ProjectListItem[] = [];

export function useProjectList(options: { enabled?: boolean; workspace?: string } = {}) {
  const { computeNode } = useAgentContext();
  // `workspace`: only that workspace's projects (`useActiveWorkspace().scopeId`) — for
  // a surface that LISTS projects to pick from. A lookup (id → name, cwd → project)
  // passes none. While only the default workspace exists `scopeId` is undefined, so
  // the request is the one from before workspaces.
  const workspace = options.workspace;
  const params = workspace ? { nodeId: computeNode?.id ?? '', workspace } : { nodeId: computeNode?.id ?? '' };
  const result = useLazyAsset(LazyAsset.DiscoveredProjects, params, {
    enabled: options.enabled !== false && !!computeNode?.id,
  });
  return { projects: result.data?.projects ?? NO_PROJECTS, totalCount: result.data?.total_count ?? 0,
    isLoading: result.isLoading, error: result.error?.message ?? null, refetch: result.reload };
}
export const useClaudeProjectList = useProjectList;

interface UseClaudeProjectResourcesOptions { includeSessions?: boolean; enabled?: boolean; pollInterval?: number }
export function useClaudeProjectResources(projectEncodedName: string | null, options: UseClaudeProjectResourcesOptions = {}) {
  const { computeNode } = useAgentContext();
  const { includeSessions = true, enabled = true, pollInterval = 0 } = options;
  const params = { nodeId: computeNode?.id ?? '', encodedName: projectEncodedName ?? '', includeSessions };
  const active = enabled && !!computeNode?.id && !!projectEncodedName;
  const result = useLazyAsset(LazyAsset.ProjectResources, params, { enabled: active });
  const reload = result.reload;
  useEffect(() => {
    if (!active || pollInterval <= 0) return;
    const id = setInterval(() => { void reload().catch(() => {}); }, pollInterval);
    return () => clearInterval(id);
  }, [active, pollInterval, reload]);
  return { data: result.data ?? null, isLoading: result.isLoading, error: result.error?.message ?? null,
    refetch: result.reload,
    invalidate: () => { void lazyAssets.client.invalidateQueries({
      queryKey: lazyAssets.key(LazyAsset.ProjectResources, params), exact: true, refetchType: 'none',
    }); },
  };
}

/**
 * Combined hook that provides both project list and selected project resources.
 * This is the main hook to use for the Projects tab UI.
 */
export function useClaudeProjects(selectedProjectEncodedName: string | null = null) {
  const projectList = useProjectList();
  const projectResources = useClaudeProjectResources(selectedProjectEncodedName);

  return {
    // Project list (fast, ~50ms)
    projects: projectList.projects,
    totalCount: projectList.totalCount,
    isLoadingProjects: projectList.isLoading,
    projectsError: projectList.error,
    refetchProjects: projectList.refetch,

    // Selected project resources (lazy, ~100ms when selected)
    selectedProject: projectResources.data,
    isLoadingResources: projectResources.isLoading,
    resourcesError: projectResources.error,
    refetchResources: projectResources.refetch,
    invalidateResources: projectResources.invalidate,
  };
}

/**
 * Get a display name for a Claude Code project.
 * Converts encoded names like "-Users-alice-Documents-dev-test" to "~/Documents/dev/test"
 */
export function getProjectDisplayName(project: ProjectListItem): string {
  // Use the last folder name from cwd
  if (project.cwd) {
    const trimmed = project.cwd.replace(/\/+$/, '');
    const lastSlash = trimmed.lastIndexOf('/');
    if (lastSlash >= 0) {
      return trimmed.substring(lastSlash + 1);
    }
    return trimmed;
  }

  // Use the name field if available and not just the encoded name
  if (project.name && project.name !== project.encoded_name) {
    return project.name;
  }

  // Fallback: decode the encoded name and take last segment
  const decoded = project.encoded_name.replace(/-/g, '/').replace(/^\/+/, '');
  const lastSlash = decoded.lastIndexOf('/');
  return lastSlash >= 0 ? decoded.substring(lastSlash + 1) : decoded;
}
